/**
 * §58 PR 4 — EXPRESS: the hauler works for ONE customer (profile.expressCustomer). The server
 * refuses anything else (403 EXPRESS_CUSTOMER_ONLY); these helpers make the pickers say so
 * up front, by offering only that customer and locking the field when nothing else is
 * possible. Only PICKUP sites are limited: drop-off destinations of any customer stay
 * pickable (api services/express-customer.ts EXPRESS_KEEP_OTHER_CUSTOMERS_DROPOFF_SITES).
 * No plan or tier name is shown anywhere (§58).
 */

import type { ExpressCustomer, UserProfile } from '@lodex-pro/shared';

import { useUserProfile } from './use-user-timezone';

// The profile OMITS expressCustomer when the server could not read it. Like the features
// (use-features.ts, §58 PR 3), keep the last-known value for the signed-in user AND tenant
// rather than read absence as "not on EXPRESS" and unlock the pickers. The server refuses
// either way.
let lastKnown: { key: string; express: ExpressCustomer | null } | null = null;

// The same key as use-features.ts (§58 PR 6a): one company's customer never shows in another.
function memoryKey(profile: UserProfile): string {
  return `${profile.userId}/${profile.tenantId ?? ''}`;
}

/** Tests only. */
export function resetExpressMemoryForTests(): void {
  lastKnown = null;
}

/** The profile's Express customer, or the last one seen for this user when it is omitted. */
export function expressFromProfile(profile: UserProfile | undefined): ExpressCustomer | null {
  if (profile === undefined) return null;
  if (profile.expressCustomer !== undefined) {
    lastKnown = { key: memoryKey(profile), express: profile.expressCustomer };
    return profile.expressCustomer;
  }
  return lastKnown !== null && lastKnown.key === memoryKey(profile) ? lastKnown.express : null;
}

/** The hauler's Express customer, or null (not on EXPRESS, or nothing known yet). */
export function useExpressCustomer(): ExpressCustomer | null {
  return useExpressCustomerState().express;
}

/**
 * The Express customer plus `pending`: true while the profile has not loaded. A picker or an
 * "add" action stays disabled while pending, so it is never briefly unlocked before the
 * profile says whether the hauler is on EXPRESS.
 */
export function useExpressCustomerState(): {
  readonly express: ExpressCustomer | null;
  readonly pending: boolean;
} {
  const { data } = useUserProfile();
  return { express: expressFromProfile(data?.profile), pending: data === undefined };
}

/** Customer options (value = hauler_operators.id) limited to the Express customer. */
export function onlyExpressCustomers<T extends { readonly value: string }>(
  options: ReadonlyArray<T>,
  express: ExpressCustomer | null,
): T[] {
  if (express === null) return [...options];
  const ids = new Set(express.haulerOperatorIds);
  return options.filter((o) => ids.has(o.value));
}

/**
 * Pickup site options limited to the Express customer: a site by its customer row, a well by
 * its catalog operator. Anything else (legacy sites with no customer) cannot be its work.
 */
export function onlyExpressPickupSites<
  T extends {
    readonly haulerOperatorId: string | null;
    readonly operatorId: string | null;
  },
>(sites: ReadonlyArray<T>, express: ExpressCustomer | null): T[] {
  if (express === null) return [...sites];
  const ids = new Set(express.haulerOperatorIds);
  return sites.filter((s) =>
    s.haulerOperatorId !== null ? ids.has(s.haulerOperatorId) : s.operatorId === express.operatorId,
  );
}

/** The one customer row to preselect and lock, when EXPRESS leaves exactly one. */
export function lockedExpressCustomerId(express: ExpressCustomer | null): string | null {
  return express !== null && express.haulerOperatorIds.length === 1
    ? (express.haulerOperatorIds[0] ?? null)
    : null;
}
